import type { Filter } from '@/types';
import { type FocusViewParams } from '@/lib/viewClientUtils';

// Focus views opened from a discussion or a conversation are recognized by their title, so the
// buttons that open them and the banners that show while they are open stay in sync without a
// dedicated URL parameter.
export const discussionFocusTitle = (discussionId: number) => `Discussion #${discussionId}`;
export const relatedUsersFocusTitle = (userId: number) => `Related Users #${userId}`;

/** The start of the person's first message in a chat focused on a discussion; the trailing space lets them keep typing. */
export function discussionFocusDraft({
	discussionId,
	title,
	referenceUserId,
}: {
	discussionId: number;
	title: string | null;
	referenceUserId: number | null;
}) {
	const trimmedTitle = title?.trim();
	return `Focus on discussion user#${discussionId}${trimmedTitle ? ` "${trimmedTitle}"` : ''}${referenceUserId ? `, from user#${referenceUserId}` : ''}. `;
}

export const isDiscussionFocus = (
	focusViewTitle: string | null | undefined,
	discussionId: number | null | undefined,
) => !!discussionId && focusViewTitle === discussionFocusTitle(discussionId);

/** Focus mode on one scenario: the user, the conversation it was copied from, and every copy of it. */
export function relatedWorkFocusParams({
	userId,
	rootId,
	referencedUserIds = [],
}: {
	userId: number;
	/** The conversation the copies were made from (`testInitialUserId`); the user itself when it is the original. */
	rootId: number;
	referencedUserIds?: number[];
}): FocusViewParams {
	return {
		title: relatedUsersFocusTitle(userId),
		filters: [
			{ field: 'testInitialUserId', operator: 'eq', value: rootId },
			{ field: 'testDiscussionUserId', operator: 'eq', value: userId },
			{ field: 'id', operator: 'eq', value: rootId },
			{ field: 'id', operator: 'eq', value: userId },
			...referencedUserIds.map((id) => ({ field: 'id', operator: 'eq' as const, value: id })),
		],
		columns: ['userType', 'testStatus', 'lastResponseAt', 'reviewNotes'],
		sortBy: 'lastResponseAt',
		sortOrder: 'desc',
		logicalOperator: 'or',
		selectedUserId: userId,
	};
}

export const isRelatedUsersFocus = (
	focusViewTitle: string | null | undefined,
	userId: number | null | undefined,
) => !!userId && focusViewTitle === relatedUsersFocusTitle(userId);

const isScenarioFocusTitle = (title: string | null | undefined) => {
	const id = Number(title?.split('#')[1]);
	return (
		Number.isInteger(id) && (title === relatedUsersFocusTitle(id) || title === discussionFocusTitle(id))
	);
};

const SCENARIO_LINK_FIELDS = ['id', 'testInitialUserId', 'testDiscussionUserId'] as const;
type ScenarioLinkField = (typeof SCENARIO_LINK_FIELDS)[number];
const isScenarioLinkField = (field: string): field is ScenarioLinkField =>
	(SCENARIO_LINK_FIELDS as readonly string[]).includes(field);

/** True when a "Related Users #N" or "Discussion #N" focus view is open and its list already shows `user`. */
export function scenarioFocusIncludes(
	focus: {
		isFocusView: boolean;
		focusViewTitle: string | null | undefined;
		filters: Filter[];
		logicalOperator: 'and' | 'or' | null;
	},
	user: { id: number; testInitialUserId?: number | null; testDiscussionUserId?: number | null },
): boolean {
	if (!focus.isFocusView || !isScenarioFocusTitle(focus.focusViewTitle) || focus.logicalOperator !== 'or') {
		return false;
	}
	return focus.filters.some(
		(filter) =>
			filter.operator === 'eq' &&
			isScenarioLinkField(filter.field) &&
			user[filter.field] != null &&
			Number(filter.value) === user[filter.field],
	);
}

type DiscussionLinked = { id?: number; userType?: string | null; testDiscussionUserId?: number | null };

/** The discussion a conversation belongs to: itself when it is one, else the one it is linked to. */
export const discussionOf = (user: DiscussionLinked): number | null =>
	(user.userType === 'discussion' ? user.id : user.testDiscussionUserId) ?? null;

/**
 * The discussion a new test user joins when its creator names none (docs/users.md, "Linking to a
 * discussion"). Undefined leaves a copy to the create route, which keeps its source's own link.
 */
export function defaultDiscussionLink({
	userType,
	cloneFromUserId,
	openUser,
	focusedUser,
}: {
	userType?: string;
	cloneFromUserId?: number;
	openUser: DiscussionLinked | null;
	/** The user in the discussion panel, which is sometimes an eval rather than a discussion. */
	focusedUser: DiscussionLinked | null;
}): number | undefined {
	const focusedDiscussionId = (focusedUser?.userType === 'discussion' && focusedUser.id) || undefined;
	if (userType === 'discussion') return undefined;
	if (cloneFromUserId == null) return focusedDiscussionId;
	if (openUser?.id !== cloneFromUserId) return undefined;
	return discussionOf(openUser) ?? focusedDiscussionId;
}
